import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { NotificationItemType, NotificationType } from "types/loadedData";

interface NotificationState {
  notifications: NotificationItemType[];
  unreadCount: number;
  loaded: boolean;
}

const sortNotifications = (notifications: NotificationItemType[]) =>
  [...notifications].sort(
    (first, second) =>
      new Date(second.createdAt || "").getTime() -
      new Date(first.createdAt || "").getTime()
  );

const upsertNotificationList = (
  notifications: NotificationItemType[],
  notification: NotificationItemType
) => {
  const nextNotifications = notifications.filter(
    (item) => item._id !== notification._id
  );

  return sortNotifications([notification, ...nextNotifications]);
};

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loaded: false,
};

export const notification = createSlice({
  name: "notification",
  initialState,
  reducers: {
    setNotifications: (state, action: PayloadAction<NotificationItemType[]>) => {
      state.notifications = sortNotifications(action.payload || []);
      state.loaded = true;
    },
    appendNotifications: (
      state,
      action: PayloadAction<NotificationItemType[]>
    ) => {
      const merged = [...state.notifications];
      action.payload.forEach((notification) => {
        const index = merged.findIndex((item) => item._id === notification._id);
        if (index >= 0) merged[index] = notification;
        else merged.push(notification);
      });
      state.notifications = sortNotifications(merged);
      state.loaded = true;
    },
    upsertNotification: (
      state,
      action: PayloadAction<NotificationItemType>
    ) => {
      const existingIndex = state.notifications.findIndex(
        (item) => item._id === action.payload._id
      );
      const existingNotification =
        existingIndex >= 0 ? state.notifications[existingIndex] : null;
      const wasUnread = Boolean(existingNotification && !existingNotification.readAt);
      const isUnread = Boolean(!action.payload.readAt);

      state.notifications = upsertNotificationList(
        state.notifications,
        action.payload
      );

      if (!existingNotification && isUnread) {
        state.unreadCount = Math.max(0, state.unreadCount + 1);
      } else if (existingNotification && wasUnread && !isUnread) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      } else if (existingNotification && !wasUnread && isUnread) {
        state.unreadCount = Math.max(0, state.unreadCount + 1);
      }
    },
    updateNotificationRead: (
      state,
      action: PayloadAction<{ notificationId: string; readAt?: string | null }>
    ) => {
      const target = state.notifications.find(
        (notification) => notification._id === action.payload.notificationId
      );

      if (target && !target.readAt) {
        target.readAt = action.payload.readAt || new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllNotificationsRead: (state) => {
      const readAt = new Date().toISOString();
      state.notifications = state.notifications.map((notification) => ({
        ...notification,
        readAt: notification.readAt || readAt,
      }));
      state.unreadCount = 0;
    },
    setNotificationUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = Math.max(0, action.payload || 0);
    },
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.loaded = false;
    },
    setNotificationTypeRead: (
      state,
      action: PayloadAction<{ type?: NotificationType }>
    ) => {
      if (!action.payload.type) return;

      const readAt = new Date().toISOString();
      state.notifications = state.notifications.map((notification) =>
        notification.type === action.payload.type && !notification.readAt
          ? { ...notification, readAt }
          : notification
      );
      state.unreadCount = state.notifications.filter(
        (notification) => !notification.readAt
      ).length;
    },
  },
});

export const {
  setNotifications,
  appendNotifications,
  upsertNotification,
  updateNotificationRead,
  markAllNotificationsRead,
  setNotificationUnreadCount,
  clearNotifications,
  setNotificationTypeRead,
} = notification.actions;

export default notification.reducer;
